/**
 * useTemplateMutations Hook (Story 5.14)
 *
 * TanStack mutations for report template CRUD:
 * - createTemplate: POST /api/report-templates
 * - updateTemplate: PUT /api/report-templates/:id
 * - deleteTemplate: DELETE /api/report-templates/:id
 *
 * All mutations invalidate the ["report-templates"] query key on success
 * for instant gallery refresh.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPut, apiDelete } from "../lib/apiClient";

// ============================================================================
// Types
// ============================================================================

export interface CreateTemplatePayload {
  name: string;
  description?: string | null;
  category?: string | null;
  layoutJson: string;
}

export interface UpdateTemplatePayload {
  id: string;
  name?: string;
  description?: string | null;
  category?: string | null;
}

export interface TemplateResponse {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  layoutJson: string;
  tenantId: string;
  isSystemTemplate: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Hook
// ============================================================================

export function useTemplateMutations() {
  const queryClient = useQueryClient();

  const createTemplate = useMutation({
    mutationFn: async (payload: CreateTemplatePayload): Promise<TemplateResponse> => {
      const res = await apiPost("/api/report-templates", payload);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const detail = (body as Record<string, string> | null)?.detail
          ?? `Failed to save template (${res.status})`;
        throw new Error(detail);
      }
      const json = (await res.json()) as { data: { template: TemplateResponse } };
      return json.data.template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-templates"] });
    },
  });

  const updateTemplate = useMutation({
    mutationFn: async (payload: UpdateTemplatePayload): Promise<TemplateResponse> => {
      const { id, ...body } = payload;
      const res = await apiPut(`/api/report-templates/${id}`, body);
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        const detail = (errBody as Record<string, string> | null)?.detail
          ?? `Failed to update template (${res.status})`;
        throw new Error(detail);
      }
      const json = (await res.json()) as { data: { template: TemplateResponse } };
      return json.data.template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-templates"] });
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await apiDelete(`/api/report-templates/${id}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const detail = (body as Record<string, string> | null)?.detail
          ?? `Failed to delete template (${res.status})`;
        throw new Error(detail);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-templates"] });
    },
  });

  return { createTemplate, updateTemplate, deleteTemplate };
}
